import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Token Storage with Security Considerations
 * 
 * SECURITY NOTE: This implementation uses browser storage (sessionStorage/localStorage)
 * which is vulnerable to XSS attacks. For enhanced security in production:
 * 
 * 1. Use httpOnly cookies set by the backend (requires backend changes)
 * 2. Implement Content Security Policy (CSP) headers
 * 3. Use short-lived access tokens with refresh token rotation
 * 
 * Current mitigations:
 * - sessionStorage preferred (cleared on tab close)
 * - localStorage only used for explicit "remember me"
 * - Tokens cleared on logout
 */
const TokenStorage = {
  // Use in-memory storage as primary for current session security
  _memoryToken: null,
  
  get: () => {
    // Priority: memory > sessionStorage > localStorage
    if (TokenStorage._memoryToken) {
      return TokenStorage._memoryToken;
    }
    return sessionStorage.getItem('auth_token') || localStorage.getItem('token');
  },
  set: (token, remember = false) => {
    TokenStorage._memoryToken = token;
    sessionStorage.removeItem('admin_session_token'); // Q8-1: a customer sign-in ends any admin session in this tab
    // Store in sessionStorage for tab persistence
    sessionStorage.setItem('auth_token', token);
    if (remember) {
      // Only persist to localStorage if explicitly requested
      localStorage.setItem('token', token);
    }
  },
  clear: () => {
    TokenStorage._memoryToken = null;
    sessionStorage.removeItem('auth_token');
    localStorage.removeItem('token');
    sessionStorage.removeItem('admin_session_token'); // Q8-1: customer sign-out also ends any admin session in this tab
  }
};

// The signed-in USER's token for the user's own resources (photos). Never returns an admin session token.
export const getUserToken = () => TokenStorage.get();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(TokenStorage.get());
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      window.__insulIdentify?.(response.data?.id);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token, fetchUser]);

  const login = async (email, password, rememberMe = true) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData, account_recovered, needs_password_reset, needs_profile_completion, needs_email_verification, recovery_message, show_onboarding } = response.data;
    TokenStorage.set(access_token, rememberMe);
    setToken(access_token);
    setUser(userData);
    window.__insulIdentify?.(userData?.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    // Return additional info about account recovery, profile completion, and onboarding
    return { 
      user: userData, 
      account_recovered: account_recovered || false,
      needs_password_reset: needs_password_reset || false,
      needs_profile_completion: needs_profile_completion || false,
      needs_email_verification: needs_email_verification || false,
      recovery_message: recovery_message || null,
      show_onboarding: show_onboarding || false
    };
  };

  const signup = async (email, password, isAdmin = false, privacyPolicyConsent = false, inviteCode = null) => {
    const response = await axios.post(`${API_URL}/api/auth/signup`, { 
      email, 
      password, 
      privacy_policy_consent: privacyPolicyConsent,
      ...(inviteCode ? { invite_code: inviteCode } : {})
    });
    const { access_token, user: userData, needs_email_verification } = response.data;
    TokenStorage.set(access_token, true);
    setToken(access_token);
    setUser(userData);
    window.__insulIdentify?.(userData?.id);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    // New users need email verification and profile completion
    return { user: userData, needs_email_verification: needs_email_verification || true };
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const logout = () => {
    TokenStorage.clear();
    setToken(null);
    setUser(null);
    window.__insulResetAnalytics?.();
    delete axios.defaults.headers.common['Authorization'];
  };

  return (
    <AuthContext.Provider value={{ user, token, login, signup, logout, loading, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};